import type { User, UserSettings } from '@koridor/shared';
import { normalizeRoomCode } from '@koridor/shared';
import type { MatchState, GameMove } from '@koridor/game-engine';

export type ConnectionState = 'online' | 'offline' | 'reconnected';

const OFFLINE_BANNER_DELAY_MS = 2500;
const RECONNECTED_BANNER_MS = 2400;
const RECONNECT_BASE_MS = 1500;
const RECONNECT_MAX_MS = 30_000;

function isNativeRefreshStorage(): boolean {
  if (typeof window === 'undefined') return false;
  const cap = (window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return !!cap?.isNativePlatform?.();
}

/** Capacitor WebView uses capacitor://localhost — API lives on production. */
export function resolveApiBaseUrl(explicit = ''): string {
  if (explicit) return explicit.replace(/\/$/, '');
  if (typeof window === 'undefined') return 'https://quoridorapp.ru';
  const cap = (window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  if (cap?.isNativePlatform?.()) return 'https://quoridorapp.ru';
  const origin = window.location.origin;
  if (
    origin.startsWith('capacitor://')
    || origin.startsWith('ionic://')
    || origin === 'http://localhost'
    || origin === 'https://localhost'
  ) {
    return 'https://quoridorapp.ru';
  }
  return origin;
}

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public field: string | null = null,
    public status = 400,
  ) {
    super(message);
  }
}

/** Normalize API / Fastify error bodies into code + message. */
export function parseApiErrorPayload(
  data: Record<string, unknown>,
  status: number,
): { code: string; message: string; field: string | null } {
  const raw = data.error;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const obj = raw as { code?: string; message?: string; field?: string | null };
    return {
      code: obj.code ?? 'UNKNOWN',
      message: obj.message ?? (typeof data.message === 'string' ? data.message : 'Request failed'),
      field: obj.field ?? null,
    };
  }
  if (typeof raw === 'string') {
    return {
      code: status === 429 ? 'RATE_LIMITED' : String(data.statusCode ?? status),
      message: typeof data.message === 'string' ? data.message : raw,
      field: null,
    };
  }
  return {
    code: status === 429 ? 'RATE_LIMITED' : 'UNKNOWN',
    message: typeof data.message === 'string' ? data.message : 'Request failed',
    field: null,
  };
}

export class KoridorClient {
  private baseUrl: string;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private ws: WebSocket | null = null;
  private wsListeners = new Map<string, Set<(payload: unknown) => void>>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private offlineBannerTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectedBannerTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private refreshInFlight: Promise<void> | null = null;
  private wasWsConnected = false;
  private activeMatchId: string | null = null;
  private reconnectAttempt = 0;
  onConnectionChange: ((state: ConnectionState) => void) | null = null;

  constructor(baseUrl = '') {
    this.baseUrl = resolveApiBaseUrl(baseUrl);
    if (typeof localStorage !== 'undefined') {
      if (isNativeRefreshStorage()) {
        this.refreshToken = localStorage.getItem('koridor_refresh');
      }
      localStorage.removeItem('koridor_access');
    }
  }

  setTokens(access: string, refresh: string) {
    this.accessToken = access;
    if (isNativeRefreshStorage()) {
      this.refreshToken = refresh;
      localStorage.setItem('koridor_refresh', refresh);
    } else {
      this.refreshToken = null;
      localStorage.removeItem('koridor_refresh');
    }
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('koridor_access');
      localStorage.removeItem('koridor_refresh');
    }
    this.disconnectWs();
  }

  get isAuthenticated() {
    return !!this.accessToken;
  }

  async restoreSession(): Promise<boolean> {
    const result = await this.restoreSessionDetailed();
    return result === 'authenticated';
  }

  /**
   * Cold-start session restore with failure reason.
   * - authenticated: access token ready
   * - network: refresh could not reach the server (keep local caches)
   * - rejected: server refused refresh (401) — clear local auth caches
   * - none: no refresh material available
   */
  async restoreSessionDetailed(): Promise<'authenticated' | 'network' | 'rejected' | 'none'> {
    if (this.accessToken) return 'authenticated';
    if (isNativeRefreshStorage() && !this.refreshToken) return 'none';
    try {
      await this.refreshTokensOnce();
      return this.accessToken ? 'authenticated' : 'rejected';
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 0 || e.code === 'NETWORK_ERROR') return 'network';
        if (e.status === 401 || e.code === 'AUTH_NO_REFRESH') return 'rejected';
      }
      return 'network';
    }
  }

  setActiveMatchId(matchId: string | null) {
    this.activeMatchId = matchId;
  }

  private clearConnectionTimers() {
    if (this.offlineBannerTimer) clearTimeout(this.offlineBannerTimer);
    if (this.reconnectedBannerTimer) clearTimeout(this.reconnectedBannerTimer);
    this.offlineBannerTimer = null;
    this.reconnectedBannerTimer = null;
  }

  private setConnectionState(state: ConnectionState) {
    this.onConnectionChange?.(state);
  }

  private emitWsEvent(type: string, payload: unknown) {
    const listeners = this.wsListeners.get(type);
    if (!listeners) return;
    listeners.forEach((fn) => fn(payload));
  }

  private async resyncActiveMatch() {
    if (!this.activeMatchId) return;
    try {
      const m = await this.getMatchState(this.activeMatchId) as {
        state: MatchState;
        status?: string;
        winnerId?: string | null;
        endReason?: string;
        summary?: unknown;
        p1TimeLeftMs?: number;
        p2TimeLeftMs?: number;
      };
      const matchId = this.activeMatchId;
      if (m.status === 'finished' || m.status === 'abandoned') {
        this.emitWsEvent('match.ended', {
          matchId,
          winnerId: m.winnerId ?? null,
          reason: m.endReason,
          summary: m.summary,
        });
        return;
      }
      this.emitWsEvent('match.state', {
        matchId,
        state: m.state,
        clock: {
          p1TimeLeftMs: m.p1TimeLeftMs,
          p2TimeLeftMs: m.p2TimeLeftMs,
        },
      });
    } catch {
      /* ignore resync errors */
    }
  }

  private async refreshTokensOnce(): Promise<void> {
    const canCookieRefresh = !isNativeRefreshStorage();
    if (!this.refreshToken && !canCookieRefresh) {
      throw new ApiError('AUTH_NO_REFRESH', 'No refresh token', null, 401);
    }
    if (this.refreshInFlight) return this.refreshInFlight;
    this.refreshInFlight = (async () => {
      let res: Response;
      try {
        res = await fetch(`${this.baseUrl}/api/v1/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.refreshToken ? { refreshToken: this.refreshToken } : {}),
        });
      } catch {
        throw new ApiError('NETWORK_ERROR', 'Нет связи с сервером', null, 0);
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        this.clearTokens();
        const err = parseApiErrorPayload(data as Record<string, unknown>, res.status);
        throw new ApiError(err.code, err.message, err.field, res.status);
      }
      const access = (data as { accessToken: string }).accessToken;
      const refresh = (data as { refreshToken: string }).refreshToken;
      if (access && refresh) this.setTokens(access, refresh);
      else if (access) this.accessToken = access;
    })().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private async request<T>(path: string, opts: RequestInit = {}, _retried = false): Promise<T> {
    const headers: Record<string, string> = {
      ...(opts.headers as Record<string, string>),
    };
    if (opts.body != null && opts.body !== '') {
      headers['Content-Type'] = headers['Content-Type'] ?? 'application/json';
    }
    if (this.accessToken) headers.Authorization = `Bearer ${this.accessToken}`;

    let res: Response;
    try {
      res = await fetch(`${this.baseUrl}${path}`, { ...opts, headers, credentials: 'include' });
    } catch {
      throw new ApiError('NETWORK_ERROR', 'Нет связи с сервером', null, 0);
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const canRefresh = !_retried && (this.refreshToken || !isNativeRefreshStorage());
      if (res.status === 401 && canRefresh) {
        await this.refreshTokensOnce();
        return this.request<T>(path, opts, true);
      }
      const err = parseApiErrorPayload(data as Record<string, unknown>, res.status);
      throw new ApiError(err.code, err.message, err.field, res.status);
    }
    return data as T;
  }

  async register(nickname: string, email: string, password: string) {
    const data = await this.request<{ accessToken: string; refreshToken: string; user: User }>('/api/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify({ nickname, email, password }),
    });
    this.setTokens(data.accessToken, data.refreshToken);
    return data;
  }

  async login(email: string, password: string) {
    const data = await this.request<{ accessToken: string; refreshToken: string; user: User }>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setTokens(data.accessToken, data.refreshToken);
    return data;
  }

  async appleSignIn(identityToken: string, nickname?: string) {
    const data = await this.request<{ accessToken: string; refreshToken: string; user: User }>('/api/v1/auth/apple', {
      method: 'POST',
      body: JSON.stringify({ identityToken, nickname }),
    });
    this.setTokens(data.accessToken, data.refreshToken);
    return data;
  }

  async logout() {
    const canCookieLogout = !isNativeRefreshStorage();
    if (this.accessToken || this.refreshToken || canCookieLogout) {
      const body = this.refreshToken ? JSON.stringify({ refreshToken: this.refreshToken }) : '{}';
      await fetch(`${this.baseUrl}/api/v1/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {}),
        },
        body,
      }).catch(() => {});
    }
    this.clearTokens();
  }

  async requestPasswordReset(email: string) {
    return this.request<{ ok: boolean }>('/api/v1/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, password: string) {
    return this.request<{ ok: boolean }>('/api/v1/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  async deleteAccount(password?: string) {
    return this.request<{ ok: boolean }>('/api/v1/me', {
      method: 'DELETE',
      body: JSON.stringify(password ? { password } : {}),
    });
  }

  async getMe() {
    return this.request<User>('/api/v1/me');
  }

  async getOnlineCount() {
    return this.request<{ count: number }>('/api/v1/presence/online-count');
  }

  async updateSettings(settings: Partial<UserSettings>) {
    return this.request<UserSettings>('/api/v1/me/settings', { method: 'PATCH', body: JSON.stringify(settings) });
  }

  async updateProfile(body: { nickname?: string; avatarUrl?: string }) {
    return this.request<User>('/api/v1/me', { method: 'PATCH', body: JSON.stringify(body) });
  }

  async getStats(scope?: 'all' | 'ranked' | 'ai') {
    const q = scope ? `?scope=${scope}` : '';
    return this.request<{
      all: { games: number; winRate: number; streak: number; avgWalls: number; avgMoves: number; peakRating: number };
      ranked: { games: number; winRate: number; streak: number; avgWalls: number; avgMoves: number; peakRating: number };
      ai: { games: number; winRate: number; streak: number; avgWalls: number; avgMoves: number; peakRating: number };
    }>(`/api/v1/me/stats${q}`);
  }

  async getHistory(limit = 20) {
    return this.request<unknown[]>(`/api/v1/me/history?limit=${limit}`);
  }

  async getActiveMatch(opts?: { mode?: string; ranked?: boolean; boardSize?: number }) {
    const params = new URLSearchParams();
    if (opts?.mode) params.set('mode', opts.mode);
    if (opts?.ranked !== undefined) params.set('ranked', opts.ranked ? 'true' : 'false');
    if (opts?.boardSize !== undefined) params.set('boardSize', String(opts.boardSize));
    const qs = params.toString();
    return this.request<{
      id: string;
      mode: 'ai' | 'online' | 'friend' | 'local';
      boardSize: number;
      ranked: boolean;
      moveNumber: number;
      yourTurn: boolean;
      opponentLabel: string;
      yourTimeLeftMs: number;
      startedAtMs?: number;
      clock?: { p1TimeLeftMs: number; p2TimeLeftMs: number; turnStartedAtMs: number };
    } | null>(`/api/v1/me/active-match${qs ? `?${qs}` : ''}`);
  }

  async getAwards() {
    return this.request<unknown[]>('/api/v1/me/awards');
  }

  async createMatch(opts: { mode: string; boardSize: number; ranked?: boolean; opponentId?: string }) {
    return this.request<{ id: string; state: MatchState; aiLevel?: string | null }>('/api/v1/matches', { method: 'POST', body: JSON.stringify(opts) });
  }

  async getMatch(id: string) {
    return this.request<unknown>(`/api/v1/matches/${id}`);
  }

  async getMatchState(id: string) {
    return this.request<{
      id: string;
      status: string;
      mode: string;
      boardSize: number;
      state: MatchState;
      winnerId: string | null;
      endReason: string | null;
      aiLevel: string | null;
      p1TimeLeftMs: number;
      p2TimeLeftMs: number;
      turnStartedAtMs: number;
      player1Id: string | null;
      player2Id: string | null;
    }>(`/api/v1/matches/${id}/state`);
  }

  async makeMove(matchId: string, move: GameMove, idempotencyKey?: string) {
    const key = idempotencyKey ?? (typeof crypto !== 'undefined' ? crypto.randomUUID() : `${Date.now()}`);
    return this.request<{ state: MatchState }>(`/api/v1/matches/${matchId}/moves`, {
      method: 'POST',
      body: JSON.stringify({ ...move, idempotencyKey: key }),
    });
  }

  async resign(matchId: string) {
    return this.request<{ ok: boolean }>(`/api/v1/matches/${matchId}/resign`, { method: 'POST' });
  }

  async getResult(matchId: string) {
    return this.request<unknown>(`/api/v1/matches/${matchId}/result`);
  }

  async requestRematch(matchId: string) {
    return this.request<{
      matched: boolean;
      matchId?: string;
      waiting?: boolean;
      opponent?: { id: string; nickname: string; rating: number };
      boardSize?: number;
      ranked?: boolean;
      mode?: 'online' | 'friend';
    }>(
      `/api/v1/matches/${matchId}/rematch`,
      { method: 'POST' },
    );
  }

  async cancelRematch(matchId: string) {
    return this.request<{ ok: boolean }>(`/api/v1/matches/${matchId}/rematch`, { method: 'DELETE' });
  }

  async joinMatchmaking(boardSize: number, ranked: boolean) {
    return this.request<{
      matched: boolean;
      matchId?: string;
      opponent?: { id: string; nickname: string; rating: number };
    }>('/api/v1/matchmaking/queue', {
      method: 'POST',
      body: JSON.stringify({ boardSize, ranked }),
    });
  }

  async cancelMatchmaking() {
    return this.request<{ ok: boolean }>('/api/v1/matchmaking/queue', { method: 'DELETE' });
  }

  async createRoom(opts: { boardSize?: number; ranked?: boolean } = {}) {
    const { boardSize = 9, ranked = false } = opts;
    return this.request<{ id: string; code: string }>('/api/v1/rooms', { method: 'POST', body: JSON.stringify({ boardSize, ranked }) });
  }

  async joinRoom(code: string) {
    return this.request<{ matchId: string; state: MatchState }>('/api/v1/rooms/join', {
      method: 'POST',
      body: JSON.stringify({ code: normalizeRoomCode(code) }),
    });
  }

  async getFriends() {
    return this.request<User[]>('/api/v1/friends');
  }

  async getFriendRequests() {
    return this.request<unknown[]>('/api/v1/friends/requests');
  }

  async searchUsers(q: string, limit = 20) {
    const params = new URLSearchParams({ q, limit: String(limit) });
    return this.request<{
      results: Array<{
        id: string;
        publicId: string;
        nickname: string;
        rating: number;
        online: boolean;
        relationship: 'friend' | 'pending_outgoing' | 'pending_incoming' | 'none';
        requestId?: string;
      }>;
    }>(`/api/v1/users/search?${params}`);
  }

  async addFriend(opts: { nickname?: string; userId?: string; publicId?: string }) {
    return this.request<{ id: string }>('/api/v1/friends/requests', {
      method: 'POST',
      body: JSON.stringify(opts),
    });
  }

  async acceptFriend(id: string) {
    return this.request<{ ok: boolean }>(`/api/v1/friends/requests/${id}/accept`, { method: 'POST' });
  }

  async declineFriend(id: string) {
    return this.request<{ ok: boolean }>(`/api/v1/friends/requests/${id}/decline`, { method: 'POST' });
  }

  async deleteFriend(userId: string) {
    return this.request<{ ok: boolean }>(`/api/v1/friends/${userId}`, { method: 'DELETE' });
  }

  async blockFriend(userId: string) {
    return this.request<{ ok: boolean }>(`/api/v1/friends/${userId}/block`, { method: 'POST' });
  }

  async registerPushToken(platform: 'ios' | 'android', token: string) {
    return this.request<{ ok: boolean }>('/api/v1/push/token', {
      method: 'POST',
      body: JSON.stringify({ platform, token }),
    });
  }

  async getUser(id: string) {
    return this.request<import('@koridor/shared').User>(`/api/v1/users/${id}`);
  }

  async createInvite(toUserId: string, matchConfig: { boardSize: number; ranked?: boolean }) {
    return this.request<{ id: string }>('/api/v1/invites', {
      method: 'POST',
      body: JSON.stringify({ toUserId, matchConfig }),
    });
  }

  async getPendingInvites() {
    return this.request<Array<{
      id: string;
      matchConfig: { boardSize?: number; ranked?: boolean };
      fromUser: { id: string; nickname: string; rating: number };
      expiresAt: string;
    }>>('/api/v1/invites/incoming');
  }

  async acceptInvite(id: string) {
    return this.request<{
      matchId: string;
      state: unknown;
      boardSize?: number;
      ranked?: boolean;
    }>(`/api/v1/invites/${id}/accept`, { method: 'POST' });
  }

  async declineInvite(id: string) {
    return this.request<{ ok: boolean }>(`/api/v1/invites/${id}/decline`, { method: 'POST' });
  }

  async getLeaderboard(scope: 'world' | 'friends' = 'world', limit = 50) {
    return this.request<{
      rows: Array<{ position: number; id: string; nickname: string; rating: number }>;
      myPosition: number;
      myRating: number;
    }>(`/api/v1/leaderboard?scope=${scope}&limit=${limit}`);
  }

  connectWs() {
    if (this.ws?.readyState === WebSocket.OPEN) return;
    if (this.ws?.readyState === WebSocket.CONNECTING) return;
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }

    void this.connectWsAsync();
  }

  private async connectWsAsync() {
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) return;
    try {
      await this.refreshTokensOnce();
    } catch {
      /* continue with existing access token if refresh fails */
    }
    if (!this.accessToken) return;

    const proto = this.baseUrl.startsWith('https') ? 'wss' : 'ws';
    const host = this.baseUrl.replace(/^https?:\/\//, '');
    this.ws = new WebSocket(`${proto}://${host}/ws`);

    this.ws.onopen = () => {
      this.clearConnectionTimers();
      this.ws?.send(JSON.stringify({ type: 'auth', token: this.accessToken }));
      if (this.wasWsConnected) {
        this.setConnectionState('reconnected');
        this.reconnectedBannerTimer = setTimeout(() => {
          this.setConnectionState('online');
          this.reconnectedBannerTimer = null;
        }, RECONNECTED_BANNER_MS);
      } else {
        this.setConnectionState('online');
      }
      this.wasWsConnected = true;
      this.reconnectAttempt = 0;
      void this.resyncActiveMatch();
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = setInterval(() => {
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ type: 'presence.ping', payload: {}, token: this.accessToken }));
        }
      }, 20_000);
    };

    this.ws.onclose = () => {
      this.clearConnectionTimers();
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = null;
      this.ws = null;
      this.offlineBannerTimer = setTimeout(() => {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
          this.setConnectionState('offline');
        }
        this.offlineBannerTimer = null;
      }, OFFLINE_BANNER_DELAY_MS);
      this.scheduleReconnect();
    };

    this.ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data) as { type: string; payload: unknown };
        if (msg.type === 'auth.error') {
          void this.refreshTokensOnce()
            .then(() => {
              this.disconnectWs();
              this.connectWs();
            })
            .catch(() => {
              this.disconnectWs();
              this.setConnectionState('offline');
            });
          return;
        }
        if (msg.type === 'auth.ok') return;
        this.emitWsEvent(msg.type, msg.payload);
      } catch { /* ignore */ }
    };
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** this.reconnectAttempt, RECONNECT_MAX_MS);
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.accessToken) this.connectWs();
    }, delay);
  }

  disconnectWs() {
    this.clearConnectionTimers();
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.wasWsConnected = false;
    this.reconnectAttempt = 0;
    this.activeMatchId = null;
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
  }

  on(event: string, handler: (payload: unknown) => void) {
    if (!this.wsListeners.has(event)) this.wsListeners.set(event, new Set());
    this.wsListeners.get(event)!.add(handler);
    return () => this.wsListeners.get(event)?.delete(handler);
  }

  sendWs(type: string, payload: object) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      const body = type === 'match.move'
        ? { ...payload, idempotencyKey: (payload as { idempotencyKey?: string }).idempotencyKey ?? (typeof crypto !== 'undefined' ? crypto.randomUUID() : `${Date.now()}`) }
        : payload;
      this.ws.send(JSON.stringify({ type, payload: body, token: this.accessToken }));
    }
  }
}

export const api = new KoridorClient(resolveApiBaseUrl());
